import React from 'react';

export enum AVideoType {
  Brightcove = 'Brightcove',
  Html5 = 'Html5',
}

export enum AVideoAspectRatio {
  Ratio1x1 = '1:1',
  Ratio3x2 = '3:2',
  Ratio16x9 = '16:9',
  Ratio5x2 = '5:2',
  Ratio2x3 = '2:3',
}

/** @brightcove Player ref returned by Brightcove's onSuccess callback */
export interface BrightcovePlayerRef {
  play?: () => Promise<void>;
  pause?: () => void;
  controls?: (show: boolean) => void;
  on?: (event: string, callback: () => void) => void;
  off?: (event: string, callback: () => void) => void;
  el_?: HTMLElement;
}

/** @brightcove Wrapper for Brightcove's onSuccess payload */
export interface BrightcovePlayerSuccess {
  ref: BrightcovePlayerRef;
}

/**
 * Base props shared by all player implementations.
 * Extends HTMLDivElement attributes per mi-web-ui-kit atom convention
 * (root element is a wrapper div).
 */
export interface AVideoBaseProps extends React.HTMLAttributes<HTMLDivElement> {
  aspectRatio?: AVideoAspectRatio;
  isPlaying?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  onAutoplayBlocked?: () => void;
  onEnded?: () => void;
}

/** @brightcove Props specific to the Brightcove player integration. */
export interface AVideoBrightcoveProps {
  videoId: string;
  videoAccountId: string;
  playerId?: string;
  onPlayerReady?: (player: BrightcovePlayerRef) => void;
  onPlayerError?: () => void;
}

export interface AVideoHtml5Props {
  src: string;
  poster?: string;
  title?: string;
  playsInline?: boolean;
}

/**
 * Public props for the AVideo component.
 * Discriminated union on videoType -- extend when adding new player types.
 */
export type AVideoProps =
  | ({ videoType: AVideoType.Brightcove } & AVideoBaseProps & AVideoBrightcoveProps)
  | ({ videoType?: AVideoType.Html5 } & AVideoBaseProps & AVideoHtml5Props);

/**
 * Internal props contract for the Brightcove player sub-component.
 * videoType is consumed by the registry router and stripped before passing to the player.
 */
export type AVideoBrightcoveInternalProps = AVideoBaseProps & AVideoBrightcoveProps;

export type AVideoHtml5InternalProps = AVideoBaseProps & AVideoHtml5Props;
